/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Field, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { BookmarkIcon, CreditCardIcon } from '@heroicons/react/solid'
import images from 'react-payment-inputs/images'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Badge, Button, Form as NXUIForm, Lightbox as LightboxComponent } from '@nexigendigital/nxui'
import { downloadFile, parseDateToReadable, uid, parseISO, Timer } from 'helpers/utils'
import { useAppSelector } from 'store/hooks'

import { useAccountPaymentMethodsQuery, useInvoiceQuery, useSendInvoiceEmailMutation, usePayInvoiceMutation } from 'api/billing'
import { setInvoice } from 'store/slices/billingSlice'

/**********************************************************************************************************
 *   STYLE
 **********************************************************************************************************/
import { Invoice, Breakdown, Payment, Method, Line, Summary } from './invoice.styles'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export const InvoiceComponent = () => {
    const dispatch = useDispatch()
    const { invoice: invoiceID, defaultPaymentMethod } = useAppSelector((state) => state.billing)
    const { appSession: { account } } = useAppSelector((state) => state.app)

    /*   REFS
     **********************************************************************************************************/
    const creditCardRef = useRef<FormikProps<{
        card_holder: string;
        card_number: string;
        cvc: string;
        expiry: string;
        is_default: boolean;
    }>>(null)

    /*   STATE
     **********************************************************************************************************/
    const [template, setTemplate] = useState('loading')
    const [activeTab, setActiveTab] = useState({ id: '', label: 'default', key: '', render: <></> })

    /*   QUERIES & MUTATIONS
     **********************************************************************************************************/
    const {
        data: invoice,
        isFetching: invoiceLoading,
        isSuccess: invoiceSuccess
    } = useInvoiceQuery(invoiceID, { skip: !invoiceID })
    const {
        data: paymentMethodsData = [],
        isFetching: paymentMethodsLoading
        // isSuccess: paymentMethodsSuccess
    } = useAccountPaymentMethodsQuery()
    const [sendInvoiceEmail, {
        isLoading: sendInvoiceEmailLoading
        // isSuccess: sendInvoiceEmailSuccess,
        // data: sendInvoiceEmailData
    }] = useSendInvoiceEmailMutation()
    const [payInvoice, {
        isLoading: payInvoiceLoading,
        isSuccess: payInvoiceSuccess,
        isError: payInvoiceError
    }] = usePayInvoiceMutation()

    /*   EFFECTS
    **********************************************************************************************************/
    useEffect(() => {
        if (defaultPaymentMethod) {
            setActiveTab({
                id: 'default',
                label: 'Saved Payment Method',
                key: 'default',
                render: renderDefaultPaymentMethodForm()
            })
        } else {
            setActiveTab({
                id: 'card',
                label: 'Credit/Debit Card',
                key: 'card',
                render: renderCreditCardForm()
            })
        }

        if (invoiceLoading || paymentMethodsLoading) {
            setTemplate('loading')
        }

        if (invoiceSuccess && invoice) {
            setTemplate('invoice')
        }

        if (payInvoiceSuccess) {
            setTemplate('success')
        }
    }, [payInvoiceSuccess, payInvoiceError, invoiceLoading, paymentMethodsLoading, defaultPaymentMethod])

    /*   JSX FUNCTIONS
     **********************************************************************************************************/
    function submitPayInvoice() {
        if (activeTab.key === 'card') {
            creditCardRef.current?.submitForm()
        } else {
            payInvoice({
                id: invoiceID,
                payment_method_id: 1,
                payment_method_data_id: defaultPaymentMethod?.id
            })
        }
    }

    function closeInvoice() {
        dispatch(setInvoice(undefined))
        Timer(() => {
            setTemplate('invoice')
        }, 250)
    }

    function checkPaymentRequired() {
        return invoice && (invoice.status === 'paid' || invoice.status === 'cancelled' || invoice.status === 'refunded')
    }

    function renderInvoiceStatus() {
        let color = 'secondary'

        if (!invoice) {
            return
        }

        switch (invoice.status) {
            case 'unpaid':
                color = 'alternative'
                break

            case 'paid':
                color = 'confirm'
                break

            case 'cancelled':
                color = 'attention'
                break

            default:
                break
        }
        return <Invoice.Status color={color}>{invoice.status}</Invoice.Status>
    }

    function renderCreditCardForm() {
        return (
            <Formik
                innerRef={creditCardRef}
                initialValues={{
                    card_holder: '',
                    card_number: '',
                    cvc: '',
                    expiry: '',
                    is_default: false
                }}
                validationSchema={
                    Yup.object({
                        card_holder: Yup.string().required('Required'),
                        card_number: Yup.string().required('Card Number is required'),
                        expiry: Yup.string().required('Required'),
                        is_default: Yup.boolean()
                    })
                }
                onSubmit={({ card_number, card_holder, expiry, cvc, is_default }) => {
                    const formattedExpiry = expiry.split('/')
                    payInvoice({
                        id: invoiceID,
                        payment_method_id: 4,
                        payment_data: {
                            card_holder,
                            card_number: card_number.replace(/ /g, ''),
                            expiry: `${formattedExpiry[0]}/${formattedExpiry[1]}`.replace(/ /g, ''),
                            cvv: cvc,
                            save_details: is_default
                        }
                    })
                }}
            >
                {({ isSubmitting }) => (
                    <Payment.Form>
                        <Field
                            id={'card_holder'}
                            label={'Cardholder Name'}
                            name={'card_holder'}
                            type={'text'}
                            disabled={isSubmitting}
                            autoComplete={'name'}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'card_details'}
                            label={'Card Details'}
                            expiry
                            cvc
                            disabled={isSubmitting}
                            component={NXUIForm.PaymentField}
                        />
                    </Payment.Form>
                )}
            </Formik>
        )
    }

    function renderDefaultPaymentMethodForm() {
        return (
            <Method.Default.Wrapper>
                <Method.Default.Type>
                    <svg {...images[defaultPaymentMethod!.type || 'mastercard'].props} />
                </Method.Default.Type>
                <Method.Default.Card>**** **** **** {defaultPaymentMethod!.identifier}</Method.Default.Card>
                <Method.Default.Expiry>{`${parseISO(defaultPaymentMethod!.expiry).month}/${parseISO(defaultPaymentMethod!.expiry).year}`}</Method.Default.Expiry>
            </Method.Default.Wrapper>
        )
    }

    function renderTotalSummary() {
        const { status, total_discount, total_paid, credit_used, tax, total, amount_due } = invoice!
        return (
            <Summary.Base>
                <Summary.Details>
                    {total_discount && total_discount !== '0.00' &&
                        <Line.Row>
                            <Line.Title>Discount</Line.Title>
                            <Line.Price>${total_discount}</Line.Price>
                        </Line.Row>
                    }
                    {credit_used && credit_used !== '0.00' &&
                        <Line.Row>
                            <Line.Title>Credit Applied</Line.Title>
                            <Line.Price>${credit_used}</Line.Price>
                        </Line.Row>
                    }
                    {tax && tax !== '0.00' &&
                        <Line.Row>
                            <Line.Title>GST</Line.Title>
                            <Line.Price>${tax}</Line.Price>
                        </Line.Row>
                    }
                    {total &&
                        <Line.Row>
                            <Line.Title>Total</Line.Title>
                            <Line.Price>${total}</Line.Price>
                        </Line.Row>
                    }
                    <Summary.Total.Base>
                        <Summary.Total.Title>{status === 'paid' && total_paid && total_paid !== '0.00' ? 'Total Amount Paid' : 'Total Amount Due'}</Summary.Total.Title>
                        <Summary.Total.Price>${amount_due}</Summary.Total.Price>
                    </Summary.Total.Base>
                </Summary.Details>
            </Summary.Base>
        )
    }

    function renderPaymentMethods() {
        const paymentMethodOptions = [
            {
                key: 'card',
                label: 'Credit/Debit Card',
                description: 'Pay with your credit card',
                icon: CreditCardIcon,
                render: renderCreditCardForm()
            }
        ]

        if (defaultPaymentMethod) {
            paymentMethodOptions.unshift({
                key: 'default',
                label: 'Saved Payment Method',
                description: 'Use your saved payment method',
                icon: BookmarkIcon,
                render: renderDefaultPaymentMethodForm()
            })
        }

        return paymentMethodOptions
    }

    let conditionalProps

    switch (template) {
        case 'loading':
            conditionalProps = {
                id: template,
                type: 'confirm',
                icon: 'info',
                title: 'Fetching Invoice',
                loading: 'Loading Invoice Summary...',
                close: () => closeInvoice(),
                conditions: {
                    state: !!invoiceID,
                    action: () => closeInvoice()
                }
            }
            break

        case 'success':
            conditionalProps = {
                id: template,
                type: 'success',
                icon: 'success',
                title: 'Invoice Paid',
                description: 'You can now see this reflected on the Billing page.',
                close: () => closeInvoice(),
                links: [{
                    label: 'Back to Billing',
                    color: 'primary',
                    target: '_blank',
                    href: 'https://nexigen.digital/contact'
                }]
            }
            break

        case 'invoice':
        default:
            conditionalProps = {
                id: template,
                type: 'custom',
                close: () => closeInvoice(),
                content: <>
                    <Invoice.Header>
                        <h2>Invoice <strong>{invoice?.id}</strong></h2>
                        {renderInvoiceStatus()}
                    </Invoice.Header>
                    <Invoice.Details>
                        <Invoice.Row key={uid()}>
                            <Invoice.Column.Base key={uid()}>
                                <Invoice.Column.Title>Account #</Invoice.Column.Title>
                                <Invoice.Column.Render>
                                    {account && <Badge>{account!.account_number}</Badge>}
                                </Invoice.Column.Render>
                            </Invoice.Column.Base>
                        </Invoice.Row>
                        <Invoice.Row key={uid()}>
                            <Invoice.Column.Base key={uid()}>
                                <Invoice.Column.Title>Due Date</Invoice.Column.Title>
                                <Invoice.Column.Render>{invoice ? parseDateToReadable(invoice.due_date) : ''}</Invoice.Column.Render>
                            </Invoice.Column.Base>
                            <Invoice.Column.Base key={uid()}>
                                <Invoice.Column.Title>Date Issued</Invoice.Column.Title>
                                <Invoice.Column.Render>{invoice ? parseDateToReadable(invoice?.created_at) : ''}</Invoice.Column.Render>
                            </Invoice.Column.Base>
                        </Invoice.Row>
                        {invoice?.status === 'paid'
                            ? <Invoice.Row key={uid()}>
                                <Invoice.Column.Base key={uid()}>
                                    <Invoice.Column.Title>Paid Date</Invoice.Column.Title>
                                    <Invoice.Column.Render>{invoice ? parseDateToReadable(invoice.date_paid) : ''}</Invoice.Column.Render>
                                </Invoice.Column.Base>
                            </Invoice.Row>
                            : <Invoice.Row key={uid()}>
                                <Invoice.Column.Base key={uid()}>
                                    <Invoice.Column.Title>Amount Paid</Invoice.Column.Title>
                                    <Invoice.Column.Render>${invoice?.total_paid}</Invoice.Column.Render>
                                </Invoice.Column.Base>
                            </Invoice.Row>
                        }
                    </Invoice.Details>
                    <Invoice.Breakdown>
                        <Breakdown.Title>Invoice Breakdown</Breakdown.Title>
                        <Breakdown.Content>
                            {invoice && invoice.invoice_items
                                ? invoice.invoice_items.map(({ id, description, total }) => (
                                    <Line.Row key={id}>
                                        <Line.Title>{description}</Line.Title>
                                        <Line.Price>${total}</Line.Price>
                                    </Line.Row>
                                ))
                                : ''
                            }
                        </Breakdown.Content>
                    </Invoice.Breakdown>
                    <Invoice.Footer>
                        {invoice?.status === 'unpaid' &&
                            <Payment.Base>
                                <Payment.Header>
                                    <Payment.Title>Pay this invoice</Payment.Title>
                                    <Payment.Description>Make a payment for this invoice by filling out the details
                                        below.</Payment.Description>
                                </Payment.Header>
                                <Payment.Body>
                                    <Method.Wrapper aria-label={'Tabs'}>
                                        {renderPaymentMethods().map((tab) => (
                                            <Method.Option.Wrapper
                                                key={tab.key}
                                                className={`${activeTab.label === tab.label && 'active'}`}
                                                aria-current={activeTab.label === tab.label ? 'page' : undefined}
                                                onClick={() => setActiveTab({ ...tab, id: uid() })}
                                            >
                                                <Method.Option.Label>{tab.label}</Method.Option.Label>
                                                <Method.Option.Icon.Fade when={activeTab.key}>
                                                    {activeTab.label === tab.label &&
                                                        <Method.Option.Icon.Wrapper>
                                                            <Method.Option.Icon.Check />
                                                        </Method.Option.Icon.Wrapper>
                                                    }
                                                </Method.Option.Icon.Fade>
                                            </Method.Option.Wrapper>
                                        ))}
                                    </Method.Wrapper>
                                    <Payment.Fade when={activeTab.key}>
                                        {activeTab.render}
                                    </Payment.Fade>
                                </Payment.Body>
                            </Payment.Base>
                        }
                        {invoice && renderTotalSummary()}
                    </Invoice.Footer>

                    <Invoice.Actions>
                        <div>
                            <Button
                                onClick={() => downloadFile(`client/billing/invoices/${invoice?.id}/download`, 'invoice.pdf')}>Download</Button>
                            <Button loading={sendInvoiceEmailLoading} onClick={() => sendInvoiceEmail(invoice?.id)}>Email
                                Invoice</Button>
                        </div>
                        <div>
                            <Button onClick={() => closeInvoice()}>Close</Button>
                            {!checkPaymentRequired() &&
                                <Button
                                    color={'primary'}
                                    onClick={() => submitPayInvoice()}
                                    disabled={payInvoiceLoading}
                                    loading={payInvoiceLoading}
                                >Pay Invoice</Button>}
                        </div>
                    </Invoice.Actions>
                </>,
                conditions: {
                    state: !!invoiceID,
                    action: () => closeInvoice()
                }
            }
    }

    /**********************************************************************************************************
     *   RENDER
     **********************************************************************************************************/
    return <LightboxComponent {...conditionalProps} />
}

export { InvoiceComponent as Invoice }
